import { Cartesian3 } from "cesium";
// --- Edge key helpers ---
/** Canonical edge key (smaller index first) for adjacency lookup */
export function edgeKey(a, b) {
    return a < b ? `${a}_${b}` : `${b}_${a}`;
}
// --- Adjacency ---
/**
 * Build face adjacency map from indexed triangle geometry.
 * Two faces are adjacent if they share an edge (two vertex indices).
 */
export function buildAdjacencyMap(geometry) {
    const edgeToFaces = new Map();
    const faceCount = geometry.indices.length / 3;
    for (let f = 0; f < faceCount; f++) {
        const i0 = geometry.indices[f * 3];
        const i1 = geometry.indices[f * 3 + 1];
        const i2 = geometry.indices[f * 3 + 2];
        for (const key of [edgeKey(i0, i1), edgeKey(i1, i2), edgeKey(i2, i0)]) {
            const faces = edgeToFaces.get(key);
            if (faces) {
                faces.push(f);
            }
            else {
                edgeToFaces.set(key, [f]);
            }
        }
    }
    const adjacency = new Map();
    for (let f = 0; f < faceCount; f++) {
        adjacency.set(f, new Set());
    }
    for (const faces of edgeToFaces.values()) {
        for (let i = 0; i < faces.length; i++) {
            for (let j = i + 1; j < faces.length; j++) {
                // biome-ignore lint/style/noNonNullAssertion: faces[i] is guaranteed to exist in adjacency map
                adjacency.get(faces[i]).add(faces[j]);
                // biome-ignore lint/style/noNonNullAssertion: faces[j] is guaranteed to exist in adjacency map
                adjacency.get(faces[j]).add(faces[i]);
            }
        }
    }
    return adjacency;
}
// --- Face normal ---
// Scratch variables to avoid per-call allocations in hot BFS loop
const _scratchV0 = new Cartesian3();
const _scratchV1 = new Cartesian3();
const _scratchV2 = new Cartesian3();
const _scratchE1 = new Cartesian3();
const _scratchE2 = new Cartesian3();
const _scratchCross = new Cartesian3();
/**
 * Compute unit normal of triangle face at given index.
 * Returns undefined for degenerate triangles.
 * Uses module-level scratch Cartesian3s to minimize GC pressure in BFS.
 */
export function computeFaceNormal(geometry, faceIndex) {
    const idx = faceIndex * 3;
    const v0 = Cartesian3.fromArray(geometry.vertices, geometry.indices[idx] * 3, _scratchV0);
    const v1 = Cartesian3.fromArray(geometry.vertices, geometry.indices[idx + 1] * 3, _scratchV1);
    const v2 = Cartesian3.fromArray(geometry.vertices, geometry.indices[idx + 2] * 3, _scratchV2);
    const e1 = Cartesian3.subtract(v1, v0, _scratchE1);
    const e2 = Cartesian3.subtract(v2, v0, _scratchE2);
    const cross = Cartesian3.cross(e1, e2, _scratchCross);
    const mag = Cartesian3.magnitude(cross);
    if (mag < 1e-10)
        return undefined;
    Cartesian3.divideByScalar(cross, mag, cross);
    // In ECEF, outward = same direction as position vector from Earth center.
    // Ensure normal always points outward regardless of winding order.
    // Use v0 centroid approximation (cheaper than computing full centroid).
    const dot = cross.x * v0.x + cross.y * v0.y + cross.z * v0.z;
    if (dot < 0) {
        Cartesian3.negate(cross, cross);
    }
    // Return a new Cartesian3 since scratch will be reused
    return Cartesian3.clone(cross);
}
// --- BFS expansion ---
/**
 * Expand selection from seed faces via BFS.
 * Stops at excluded faces or where angle between normals exceeds tolerance.
 *
 * @param angleToleranceRad - max angle (radians) between seed normal and neighbor normal
 */
export function expandSelection(geometry, adjacencyMap, seedFaces, excludedFaces, angleToleranceRad) {
    if (seedFaces.size === 0)
        return new Set();
    // Compute reference normal as average of seed normals
    const refNormal = new Cartesian3(0, 0, 0);
    let validSeedCount = 0;
    for (const seed of seedFaces) {
        const n = computeFaceNormal(geometry, seed);
        if (n) {
            Cartesian3.add(refNormal, n, refNormal);
            validSeedCount++;
        }
    }
    if (validSeedCount === 0)
        return new Set(seedFaces);
    Cartesian3.divideByScalar(refNormal, validSeedCount, refNormal);
    Cartesian3.normalize(refNormal, refNormal);
    const cosThreshold = Math.cos(angleToleranceRad);
    const selected = new Set(seedFaces);
    const queue = [...seedFaces];
    while (queue.length > 0) {
        // biome-ignore lint/style/noNonNullAssertion: queue.length > 0 guarantees pop() returns a value
        const current = queue.pop();
        const neighbors = adjacencyMap.get(current);
        if (!neighbors)
            continue;
        for (const neighbor of neighbors) {
            if (selected.has(neighbor) || excludedFaces.has(neighbor))
                continue;
            const normal = computeFaceNormal(geometry, neighbor);
            if (!normal)
                continue;
            if (Cartesian3.dot(refNormal, normal) >= cosThreshold) {
                selected.add(neighbor);
                queue.push(neighbor);
            }
        }
    }
    return selected;
}
/**
 * Get the centroid of a face.
 */
export function computeFaceCentroid(geometry, faceIndex) {
    const idx = faceIndex * 3;
    const i0 = geometry.indices[idx] * 3;
    const i1 = geometry.indices[idx + 1] * 3;
    const i2 = geometry.indices[idx + 2] * 3;
    return new Cartesian3((geometry.vertices[i0] + geometry.vertices[i1] + geometry.vertices[i2]) / 3, (geometry.vertices[i0 + 1] +
        geometry.vertices[i1 + 1] +
        geometry.vertices[i2 + 1]) /
        3, (geometry.vertices[i0 + 2] +
        geometry.vertices[i1 + 2] +
        geometry.vertices[i2 + 2]) /
        3);
}
/**
 * Convert selected faces to an IGeometry containing only those faces.
 * Useful for creating the topEdgesOfBaseSlope geometry from selected faces.
 */
export function selectedFacesToGeometry(geometry, selectedFaces) {
    const vertexMap = new Map();
    const vertices = [];
    const indices = [];
    const getOrAddVertex = (originalIndex) => {
        const existing = vertexMap.get(originalIndex);
        if (existing !== undefined)
            return existing;
        const newIndex = vertices.length / 3;
        const offset = originalIndex * 3;
        vertices.push(geometry.vertices[offset], geometry.vertices[offset + 1], geometry.vertices[offset + 2]);
        vertexMap.set(originalIndex, newIndex);
        return newIndex;
    };
    for (const f of selectedFaces) {
        const idx = f * 3;
        indices.push(getOrAddVertex(geometry.indices[idx]), getOrAddVertex(geometry.indices[idx + 1]), getOrAddVertex(geometry.indices[idx + 2]));
    }
    return { vertices, indices };
}
//# sourceMappingURL=mesh-face-selection.js.map